import { z } from "zod";
import { bookProductionProductSchema } from "./book-production-evidence.js";
export const BOOK_PHYSICAL_EDITION_POLICY_CONTRACT = "BOOK_PHYSICAL_EDITION_POLICY_V1";
export const BOOK_PHYSICAL_EDITION_POLICY_VERSION = "2026-09-28.1";
export const BOOK_PHYSICAL_EDITION_LAUNCH_MATRIX = Object.freeze({
    BEDTIME: {
        editionPolicyId: "bedtime-hardcover-landscape-32",
        customerFacingName: "Children's / Bedtime Book",
        formatLabel: "8 × 11 in landscape hardcover",
        interiorPageCount: 32,
        trim: { widthMm: 280, heightMm: 210, orientation: "LANDSCAPE" },
    },
    COMIC: {
        editionPolicyId: "comic-hardcover-portrait-32",
        customerFacingName: "Comic Book",
        formatLabel: "8 × 11 in portrait hardcover",
        interiorPageCount: 32,
        trim: { widthMm: 210, heightMm: 280, orientation: "PORTRAIT" },
    },
    RECIPE: {
        editionPolicyId: "recipe-hardcover-portrait-64",
        customerFacingName: "Recipe Book",
        formatLabel: "8 × 11 in portrait hardcover",
        interiorPageCount: 64,
        trim: { widthMm: 210, heightMm: 280, orientation: "PORTRAIT" },
    },
    MY_STORY: {
        editionPolicyId: "my-story-hardcover-portrait-80",
        customerFacingName: "My Story",
        formatLabel: "8 × 11 in portrait hardcover",
        interiorPageCount: 80,
        trim: { widthMm: 210, heightMm: 280, orientation: "PORTRAIT" },
    },
    OUR_STORY: {
        editionPolicyId: "our-story-hardcover-square-64",
        customerFacingName: "Our Story",
        formatLabel: "8 × 8 in square hardcover",
        interiorPageCount: 64,
        trim: { widthMm: 200, heightMm: 200, orientation: "SQUARE" },
    },
});
const safeIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(180)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.:/-]*$/u);
const positiveMillimetresSchema = z.number().positive().max(2_000);
export const bookPhysicalEditionPageRoleSchema = z
    .object({
    productionOrdinal: z.number().int().positive().max(1_000),
    roleCode: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .regex(/^[A-Z0-9][A-Z0-9_]*$/u),
    sectionCode: z
        .string()
        .trim()
        .min(1)
        .max(80)
        .regex(/^[A-Z0-9][A-Z0-9_]*$/u),
    side: z.enum(["VERSO", "RECTO"]),
    contentDisposition: z.enum(["REQUIRED", "OPTIONAL_AUTHORED", "INTENTIONAL_BLANK_ONLY"]),
    policyOwner: z.enum(["COMMON", "BEDTIME", "COMIC", "RECIPE", "MY_STORY", "OUR_STORY"]),
    sourceSlotId: safeIdSchema.nullable(),
})
    .strict();
export const bookPhysicalEditionPolicyV1Schema = z
    .object({
    contract: z.literal(BOOK_PHYSICAL_EDITION_POLICY_CONTRACT),
    policyVersion: z.literal(BOOK_PHYSICAL_EDITION_POLICY_VERSION),
    editionPolicyId: safeIdSchema,
    product: bookProductionProductSchema,
    customerFacingName: z.string().trim().min(1).max(100),
    formatLabel: z.string().trim().min(1).max(160),
    trim: z
        .object({
        widthMm: positiveMillimetresSchema,
        heightMm: positiveMillimetresSchema,
        orientation: z.enum(["PORTRAIT", "LANDSCAPE", "SQUARE"]),
    })
        .strict(),
    interiorPageCount: z.number().int().positive().max(1_000),
    canonicalCoverPageCount: z.literal(1),
    fulfillmentAssembly: z
        .object({
        frontEndpaperPageCount: z.union([z.literal(0), z.literal(1)]),
        backEndpaperPageCount: z.union([z.literal(0), z.literal(1)]),
        uploadPageCount: z.number().int().positive(),
        order: z
            .array(z.enum(["COVER", "FRONT_ENDPAPER", "INTERIOR", "BACK_ENDPAPER"]))
            .min(2)
            .max(4),
    })
        .strict(),
    pageArchitectureId: safeIdSchema,
    pageArchitectureVersion: safeIdSchema,
    pageRoles: z.array(bookPhysicalEditionPageRoleSchema).min(1).max(1_000),
    customerChoicePolicy: z.literal("FIXED_INCLUDED_HARDCOVER_NO_FORMAT_SELECTOR"),
    truthBoundary: z.literal("PHYSICAL_EDITION_POLICY_NOT_RENDERED_APPROVED_PRINTED_OR_PROVIDER_ACTIVATED"),
})
    .strict()
    .superRefine((policy, context) => {
    const expectedUploadPageCount = policy.canonicalCoverPageCount +
        policy.fulfillmentAssembly.frontEndpaperPageCount +
        policy.interiorPageCount +
        policy.fulfillmentAssembly.backEndpaperPageCount;
    if (policy.fulfillmentAssembly.uploadPageCount !== expectedUploadPageCount)
        context.addIssue({
            code: "custom",
            path: ["fulfillmentAssembly", "uploadPageCount"],
            message: "The upload count must match the declared cover, endpapers, and exact interior.",
        });
    const expectedOrder = [
        "COVER",
        ...(policy.fulfillmentAssembly.frontEndpaperPageCount ? ["FRONT_ENDPAPER"] : []),
        "INTERIOR",
        ...(policy.fulfillmentAssembly.backEndpaperPageCount ? ["BACK_ENDPAPER"] : []),
    ];
    if (policy.fulfillmentAssembly.order.length !== expectedOrder.length ||
        policy.fulfillmentAssembly.order.some((kind, index) => kind !== expectedOrder[index]))
        context.addIssue({
            code: "custom",
            path: ["fulfillmentAssembly", "order"],
            message: "The assembly order must exactly match the declared endpaper policy.",
        });
    if (policy.pageRoles.length !== policy.interiorPageCount)
        context.addIssue({
            code: "custom",
            path: ["pageRoles"],
            message: "The physical page architecture must name every interior page exactly once.",
        });
    for (const [index, page] of policy.pageRoles.entries()) {
        if (page.productionOrdinal !== index + 1)
            context.addIssue({
                code: "custom",
                path: ["pageRoles", index, "productionOrdinal"],
                message: "Physical page ordinals must be contiguous and start at one.",
            });
        const expectedSide = page.productionOrdinal % 2 === 0 ? "VERSO" : "RECTO";
        if (page.side !== expectedSide)
            context.addIssue({
                code: "custom",
                path: ["pageRoles", index, "side"],
                message: `Physical page ${page.productionOrdinal} must be ${expectedSide}.`,
            });
    }
    const roleOrdinals = policy.pageRoles.map(({ productionOrdinal }) => productionOrdinal);
    if (new Set(roleOrdinals).size !== roleOrdinals.length)
        context.addIssue({
            code: "custom",
            path: ["pageRoles"],
            message: "A physical page ordinal cannot be reused.",
        });
});
//# sourceMappingURL=book-physical-edition.js.map